import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Edit, Trash2, Search, Upload } from "lucide-react";
import { toast } from "sonner";

export default function AdminWrestlers() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/wrestlers", { params: q ? { q } : {} })
                        .then((r) => setItems(r.data || []));
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const del = async (id) => {
    if (!window.confirm("Delete this wrestler?")) return;
    try {
      await api.delete(`/admin/wrestlers/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-red-500 mb-1">Roster</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black uppercase">Wrestlers</h1>
          <p className="text-slate-400 text-sm mt-1">
            The database that powers the guess dropdown. Add primary name, former ring names,
            nicknames, tag teams and factions — the search matches all of them.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/admin")} className="border-slate-700"
                  data-testid="wrestlers-back">Back</Button>
          <Button variant="outline" onClick={() => nav("/admin/wrestlers/import")}
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
            data-testid="wrestlers-import-btn">
            <Upload className="h-4 w-4 mr-1" /> Bulk Import
          </Button>
          <Button onClick={() => nav("/admin/wrestlers/new")} data-testid="wrestlers-new-btn"
            className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase">
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        </div>
      </div>

      <div className="mt-6 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name, nickname, faction…"
          data-testid="wrestlers-filter"
          className="pl-9 bg-slate-950 border-slate-700" />
      </div>

      <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-900/70 overflow-hidden"
           data-testid="wrestlers-list">
        <div className="grid grid-cols-12 px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-slate-800">
          <div className="col-span-4">Name</div>
          <div className="col-span-2">Era</div>
          <div className="col-span-4">Aliases</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {items.length === 0 ? (
          <div className="p-6 text-slate-500" data-testid="wrestlers-empty">
            No wrestlers yet. Add one before creating puzzles.
          </div>
        ) : items.map((w) => {
          const aliases = [...(w.former_names || []), ...(w.nicknames || []),
                            ...(w.tag_teams || []), ...(w.factions || [])];
          return (
            <div key={w.id} className="grid grid-cols-12 items-center px-4 py-3 border-b border-slate-800/60"
                 data-testid={`wrestlers-row-${w.id}`}>
              <div className="col-span-4 font-display font-bold truncate">{w.primary_name}</div>
              <div className="col-span-2 text-sm text-slate-400">{w.era || "—"}</div>
              <div className="col-span-4 text-xs text-slate-500 truncate font-mono">
                {aliases.slice(0, 4).join(" · ") || "—"}
                {aliases.length > 4 && <span> +{aliases.length - 4}</span>}
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <Link to={`/admin/wrestlers/${w.id}`}>
                  <Button size="sm" variant="ghost" data-testid={`edit-w-${w.id}`}>
                    <Edit className="h-4 w-4" />
                  </Button>
                </Link>
                <Button size="sm" variant="ghost" onClick={() => del(w.id)} data-testid={`del-w-${w.id}`}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
